// Layout: the flex and grid containers, and the frame that holds a whole screen.
//
// These tags hold no content of their own — they are boxes. So, unlike the other elements,
// an <i80-flex> or <i80-grid> *is* the container: the i80-layout-* classes go on the tag
// itself and the children the author wrote are never moved. Only `tag` changes that. It
// names the element the layout belongs on (a list, a <nav>, a <span> inside a sentence);
// the tag then renders that element around the children and steps out of the layout with
// `display: contents`, because a container with `display: contents` has no layout at all.
import { I80Element, define, h } from "../base.js";

const GAPS = ["0", "4", "8", "12", "16", "24", "32", "48"];
const DIRECTIONS = ["row", "column"];
const JUSTIFYS = ["start", "center", "end", "space-between", "space-around", "space-evenly"];
const ALIGNS = ["start", "center", "end", "stretch"];
// The breakpoints a responsive grid can be told about (see Foundations/Breakpoints).
const VIEWS = ["sm", "md", "lg", "xl", "xxl"];

// Elements a layout box can reasonably be. Ember accepts any tag name; here the list is
// checked so a typo is reported instead of silently creating an unknown element.
const TAGS = ["div", "span", "p", "ul", "ol", "li", "dl", "nav", "section", "article", "aside",
  "header", "footer", "main", "figure", "form", "fieldset", "label", "summary", "td", "th"];

/** Replace the classes we manage (the ones starting with `prefix`) and keep the author's. */
function setClasses(el, prefix, classes) {
  const kept = [...el.classList].filter((name) => !name.startsWith(prefix));
  const next = [...new Set([...kept, ...classes.filter(Boolean)])].join(" ");
  if (el.className !== next) el.className = next;
}

/** A custom property, or nothing at all when there is no value. */
function setVar(el, name, value) {
  if (value) el.style.setProperty(name, value);
  else el.style.removeProperty(name);
}

/** `gap="16"` is one gap; `gap="12 24"` is a row gap and a column gap, as in CSS. */
function gaps(host) {
  const attribute = host.getAttribute("gap");
  const written = (attribute ?? "").trim().split(/\s+/).filter(Boolean);
  const values = written.map((value) => {
    if (GAPS.includes(value)) return value;
    console.warn(`<${host.localName}> gap="${attribute}" is not one of: ${GAPS.join(", ")}. Using "0".`);
    return "0";
  });
  // Ember's default gap is "0", and it always renders both gap classes: the container sets
  // `gap: var(--row-gap) var(--column-gap)`, which is invalid until something fills them.
  return [values[0] ?? "0", values[1] ?? values[0] ?? "0"];
}

/**
 * `grow` on its own means 1 and `grow="0"` means 0 — the two common cases, which have a
 * class. Any other number is a one-off, and goes in an inline style as Ember's does.
 * Returns [class suffix, inline value]; both null when the attribute is not there.
 */
function factor(host, name) {
  const value = host.getAttribute(name);
  if (value === null) return [null, null];
  if (["", "true", "1"].includes(value)) return ["1", null];
  if (["false", "0"].includes(value)) return ["0", null];
  return [null, value];
}

/**
 * A box that carries a layout: the tag itself, or the element `tag` names inside it.
 * Subclasses only compute classes and custom properties and hand them to setLayout().
 */
class LayoutBox extends I80Element {
  setup() {
    const name = this.choice("tag", TAGS, null);
    if (!name) return;
    // A tag between a list and its item, or a row and its cell, breaks the relationship the
    // browser and screen readers read from the markup — our element is still in the way,
    // display: contents or not. Write the <li>, <td> or <th> yourself as a direct child of
    // the list or row, and put i80-layout-grid-item (plus its custom properties) on it.
    if (["li", "td", "th"].includes(name)) {
      console.warn(
        `<${this.localName} tag="${name}"> leaves this element between the list or row and its `
        + `item, which loses the list or table semantics. Write the <${name}> yourself and give `
        + `it the layout class instead.`,
      );
    }
    // On a clone the element is already there, with its part marker.
    this._box = this.child('[data-i80-part="layout"]')
      ?? this.appendChild(h(name, { "data-i80-part": "layout" }, ...this.takeContent()));
  }

  /** With `tag`, children that turned up after the element was built belong in the box. */
  adopt() {
    this._box?.append(...this.takeContent());
  }

  /** The element the layout is on. Without `tag` that is the tag itself. */
  get box() {
    return this._box ?? this;
  }

  /**
   * With `tag` the tag is `display: contents`, so what the author wrote on it — its classes
   * and its inline style — has nothing to apply to and has to go on the box instead.
   */
  setLayout(prefix, classes) {
    const own = this._box ? (this.getAttribute("class") ?? "").split(/\s+/) : [];
    setClasses(this.box, prefix, [...own, ...classes]);
    if (!this._box) return;
    const style = this.getAttribute("style");
    // The layout's own custom properties are set as properties, so only the author's
    // declarations are copied, and only when they change.
    if (style === this._copiedStyle) return;
    for (const name of this._copiedNames ?? []) this._box.style.removeProperty(name);
    const holder = h("div");
    if (style) holder.setAttribute("style", style);
    this._copiedNames = [...holder.style];
    for (const name of this._copiedNames) {
      this._box.style.setProperty(name, holder.style.getPropertyValue(name), holder.style.getPropertyPriority(name));
    }
    this._copiedStyle = style;
  }
}

/**
 * <i80-flex direction="column" align="center" gap="16">
 *   <img src="avatar.png" alt="">
 *   <p>Ada Lovelace</p>
 * </i80-flex>
 *
 * The tag is the flex container, so every child you write is a flex item where you wrote
 * it. `tag` puts the layout on a list, a <nav> or a <span> instead.
 */
export class I80Flex extends LayoutBox {
  static get observedAttributes() {
    return ["direction", "justify", "align", "wrap", "gap", "inline"];
  }

  update() {
    const [row, column] = gaps(this);
    const justify = this.getAttribute("justify") && this.choice("justify", JUSTIFYS, null);
    const align = this.getAttribute("align") && this.choice("align", ALIGNS, null);
    this.setLayout("i80-layout-flex", [
      "i80-layout-flex",
      `i80-layout-flex--direction-${this.choice("direction", DIRECTIONS, "row")}`,
      justify && `i80-layout-flex--justify-content-${justify}`,
      align && `i80-layout-flex--align-items-${align}`,
      `i80-layout-flex--row-gap-${row}`,
      `i80-layout-flex--column-gap-${column}`,
      this.flag("wrap") && "i80-layout-flex--has-wrapping",
      this.flag("inline") && "i80-layout-flex--is-inline",
    ]);
  }
}

/**
 * <i80-flex-item grow shrink="0" basis="200px">…</i80-flex-item>
 *
 * Only needed for a child whose own sizing differs: without one, a child of <i80-flex> is
 * already a flex item.
 */
export class I80FlexItem extends LayoutBox {
  static get observedAttributes() {
    return ["basis", "grow", "shrink", "collapse"];
  }

  update() {
    const basis = this.getAttribute("basis");
    // A basis of zero is the common case and has a class; any other value is a length.
    const isZero = basis !== null && Number.parseFloat(basis) === 0;
    const [grow, growValue] = factor(this, "grow");
    const [shrink, shrinkValue] = factor(this, "shrink");
    this.setLayout("i80-layout-flex", [
      "i80-layout-flex-item",
      isZero && "i80-layout-flex-item--basis-0",
      grow && `i80-layout-flex-item--grow-${grow}`,
      shrink && `i80-layout-flex-item--shrink-${shrink}`,
      this.flag("collapse") && "i80-layout-flex-item--enable-collapse-below-content-size",
    ]);
    this.box.style.flexBasis = basis !== null && !isZero ? basis : "";
    this.box.style.flexGrow = growValue ?? "";
    this.box.style.flexShrink = shrinkValue ?? "";
  }
}

/**
 * <i80-grid column-min-width="240px" gap="24">…</i80-grid>
 * <i80-grid column-width-sm="100%" column-width-md="33.33%" gap="24">…</i80-grid>
 *
 * Equal columns that fill the width by default. `column-min-width` lets them stretch from
 * that width up; `column-width` keeps them at it. One `column-width-<view>` per breakpoint
 * makes the same grid responsive.
 */
export class I80Grid extends LayoutBox {
  static get observedAttributes() {
    return ["align", "gap", "column-min-width", "column-width", ...VIEWS.map((v) => `column-width-${v}`)];
  }

  update() {
    const box = this.box;
    const [row, column] = gaps(this);
    const align = this.getAttribute("align") && this.choice("align", ALIGNS, null);
    const minWidth = this.getAttribute("column-min-width");
    const width = minWidth ? null : this.getAttribute("column-width");
    const views = minWidth || width ? [] : VIEWS.filter((v) => this.getAttribute(`column-width-${v}`));
    if (minWidth && (this.getAttribute("column-width") || VIEWS.some((v) => this.getAttribute(`column-width-${v}`)))) {
      // A column has one width: a minimum it stretches from, or a width it keeps. Not both.
      console.warn(`<${this.localName}> column-min-width and column-width cannot be used together. Using column-min-width.`);
    }

    this.setLayout("i80-layout-grid", [
      "i80-layout-grid",
      align && `i80-layout-grid--align-items-${align}`,
      `i80-layout-grid--row-gap-${row}`,
      `i80-layout-grid--column-gap-${column}`,
      width && "i80-layout-grid--column-width-non-responsive",
      ...views.map((v) => `i80-layout-grid--column-width-${v}`),
    ]);
    setVar(box, "--i80-layout-grid-column-min-width", minWidth || width);
    for (const view of VIEWS) {
      setVar(box, `--i80-layout-grid-column-width-${view}`, views.includes(view) ? this.getAttribute(`column-width-${view}`) : null);
    }
  }
}

// colspan and rowspan, each with the custom property that holds it and the class that
// switches to the per-breakpoint one.
const SPANS = [
  { attribute: "colspan", variable: "column-span", view: "col-span-view" },
  { attribute: "rowspan", variable: "row-span", view: "row-span-view" },
];

/**
 * <i80-grid-item colspan="2" rowspan="2">…</i80-grid-item>
 * <i80-grid-item colspan-sm="1" colspan-md="2">…</i80-grid-item>
 *
 * How many columns and rows one item takes. Without one, a child of <i80-grid> already
 * fills a single cell.
 */
export class I80GridItem extends LayoutBox {
  static get observedAttributes() {
    return SPANS.flatMap(({ attribute }) => [attribute, ...VIEWS.map((v) => `${attribute}-${v}`)]);
  }

  update() {
    const box = this.box;
    const classes = ["i80-layout-grid-item"];
    for (const { attribute, variable, view } of SPANS) {
      const span = this.getAttribute(attribute);
      setVar(box, `--i80-layout-grid-${variable}`, span);
      for (const name of VIEWS) {
        const value = span ? null : this.getAttribute(`${attribute}-${name}`);
        setVar(box, `--i80-layout-grid-${variable}-${name}`, value);
        if (value) classes.push(`i80-layout-grid--${view}-${name}`);
      }
    }
    this.setLayout("i80-layout-grid", classes);
  }
}

// The frame's areas: the area, and the element that becomes it. `slot` is the way in for a
// page that already has its own <main> — a documentation page, a demo embedded in one.
const AREAS = [["header", "header"], ["sidebar", "aside"], ["main", "main"], ["footer", "footer"], ["modals", null]];

/**
 * <i80-app-frame>
 *   <header><i80-app-header …></i80-app-header></header>
 *   <aside><i80-app-side-nav …></i80-app-side-nav></aside>
 *   <main>…</main>
 *   <footer><i80-app-footer></i80-app-footer></footer>
 * </i80-app-frame>
 *
 * The frame is a grid of four areas, and in HTML the areas are the elements you already
 * write: <header>, <aside>, <main>, <footer>. So the tag creates nothing and moves
 * nothing — it styles what is there, and the grid closes up around anything you leave out.
 * A <div slot="modals"> becomes the container that sits over the page; <i80-modal> and
 * <i80-flyout> use the browser's top layer and don't need it.
 *
 * slot="header|sidebar|main|footer" names an area on any element, for a page that already
 * has a <main> of its own and can't have a second one (a documentation page, a demo).
 */
export class I80AppFrame extends I80Element {
  /** The areas are the author's own elements, so there is nothing to take in: update()
   *  styles whatever turned up, and the base class calls it right after this. */
  adopt() {}

  update() {
    const found = new Map(AREAS.map(([area, tag]) => [area, this.child(`[slot="${area}"]`) ?? (tag ? this.child(tag) : null)]));
    setClasses(this, "i80-app-frame", [
      "i80-app-frame",
      found.get("header") && found.get("sidebar") && "i80-app-frame--has-header-with-sidebar",
    ]);
    for (const [area, el] of found) {
      if (el) setClasses(el, "i80-app-frame__", [`i80-app-frame__${area}`]);
    }
    // The target a "Skip to main content" link jumps to, as in Helios. One per page: a
    // second frame, or a clone of this one, leaves its <main> without the id.
    const main = found.get("main");
    if (main) {
      const taken = document.getElementById("i80-main");
      if (!main.id && !taken) main.id = "i80-main";
      else if (main.id === "i80-main" && taken && taken !== main) main.removeAttribute("id");
    }
  }
}

define("i80-flex", I80Flex);
define("i80-flex-item", I80FlexItem);
define("i80-grid", I80Grid);
define("i80-grid-item", I80GridItem);
define("i80-app-frame", I80AppFrame);
